import { useLocation } from "react-router-dom";
import { DesktopSidebar } from "../app/desktopSidebar/DesktopSidebar";
import { DesktopTopNav } from "../app/desktopTopNav/DesktopTopNav";
import {
  activeDestinationOf,
  useDesktopSidebarState,
  useDesktopSidebarWidth,
} from "../app/layout/AppLayout";
import {
  DesktopArticleHeroSkeleton,
  DesktopArticleRowSkeleton,
} from "../discovery/DesktopArticleRow";
import Skeleton from "./Skeleton";

/** The page shapes a desktop boot can draw (Figma "Transverse 07"). */
export type DesktopBootPage = "gpt" | "room" | "explore";

// The four conversation starters under the composer, uneven like their labels.
const INTENT_WIDTHS = [80, 110, 80, 72] as const;

// The topic chips of a room's rail and of Explore's tab bar.
const RAIL_CHIP_WIDTHS = [64, 100, 130, 120, 90, 110] as const;
const TAB_CHIP_WIDTHS = [48, 112, 96, 128, 104, 88] as const;

/**
 * Cold-boot silhouette of the desktop shell (Figma "Transverse 07 - Loading &
 * Skeleton", D0.boot): the sidebar in its pending session, beside the page
 * the path leads to. The sidebar takes the reader's stored shape (collapsed or
 * not, and its width), so nothing moves when the real one replaces it.
 *
 * Rendered in the shell's content area: the layout draws no sidebar of its
 * own while the user loads (only once that load has failed). Pure render, like every boot skeleton: no
 * queries and no auth reads.
 */
export default function DesktopBootSkeleton({
  page,
}: {
  page: DesktopBootPage;
}): React.JSX.Element {
  const { pathname } = useLocation();
  const [state, setState] = useDesktopSidebarState();
  const [width, setWidth] = useDesktopSidebarWidth();

  return (
    <div className="flex h-full">
      <DesktopSidebar
        state={state}
        onToggle={() => {
          setState(state === "expanded" ? "collapsed" : "expanded");
        }}
        width={width}
        onWidthChange={setWidth}
        // A root boot to a pinned room is in Rooms before its redirect.
        activeDestination={
          page === "room" ? "rooms" : activeDestinationOf(pathname)
        }
        session={{ status: "pending" }}
        audioPlayer={null}
      />
      <div className="min-w-0 flex-1 overflow-hidden">
        {(() => {
          switch (page) {
            case "gpt":
              return <GptLandingSkeleton />;
            case "room":
              return <RoomSkeleton />;
            case "explore":
              return <ExploreSkeleton />;
            default:
              throw new Error(page satisfies never);
          }
        })()}
      </div>
    </div>
  );
}

/**
 * The bar over a page, its context and search trigger as blocks. The search
 * palette needs a session, so the trigger is not drawn live.
 */
function TopNavSkeleton({
  contextWidth,
}: {
  contextWidth: number;
}): React.JSX.Element {
  return (
    <DesktopTopNav
      context={<Skeleton width={contextWidth} height={16} />}
      trailing={{
        kind: "actions",
        content: <Skeleton $variant="rounded" width={280} height={36} />,
      }}
      surface="default"
      className={null}
    />
  );
}

/** Mirrors GPTNewChatPage on desktop: the greeting, the composer, the starters. */
function GptLandingSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col bg-ds-canvas">
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-2.5 pt-8 pb-72">
        <div className="flex w-full max-w-gpt-greeting justify-center px-8 py-2">
          <Skeleton $variant="rounded" width={520} height={40} />
        </div>
        <div className="relative w-full max-w-gpt-landing px-18">
          {/* The composer keeps its outline: the reader sees where to type. */}
          <div className="flex h-31 flex-col justify-between rounded-2xl border border-ds-slate-100 bg-ds-white p-4">
            <Skeleton width={320} height={14} />
            <div className="flex items-center justify-between">
              <Skeleton $variant="circular" width={24} height={24} />
              <Skeleton $variant="rounded" width={28} height={28} />
            </div>
          </div>
          <div className="absolute inset-x-0 top-full flex flex-col gap-3 px-18 pt-3">
            <div className="flex gap-2">
              {INTENT_WIDTHS.map((intentWidth, index) => (
                <Skeleton
                  key={index}
                  $variant="rounded"
                  width={intentWidth}
                  height={32}
                />
              ))}
            </div>
            <div className="flex justify-center py-2">
              <Skeleton width={330} height={12} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mirrors DesktopRoomShell on its Discovery tab. */
function RoomSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col">
      <TopNavSkeleton contextWidth={220} />
      <div className="px-10 pb-24">
        <div className="mx-auto flex max-w-desktop-block flex-col gap-10 pt-10">
          <div className="flex items-start justify-between gap-8">
            <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col gap-3">
              <Skeleton $variant="rounded" width={360} height={40} />
              <Skeleton className="rounded-full" width={160} height={24} />
              <div className="flex flex-col gap-2 pt-1">
                <Skeleton width="100%" height={16} />
                <Skeleton width="92%" height={16} />
                <Skeleton width="64%" height={16} />
              </div>
              <Skeleton width={140} height={16} />
              <div className="flex gap-3 pt-2">
                <Skeleton $variant="rounded" width={112} height={36} />
                <Skeleton $variant="rounded" width={92} height={36} />
                <Skeleton $variant="rounded" width={36} height={36} />
              </div>
            </div>
            <Skeleton className="shrink-0 rounded-xl" width={88} height={88} />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex gap-2 border-b border-ds-slate-100 pb-2">
              <Skeleton $variant="rounded" width={196} height={38} />
              <Skeleton $variant="rounded" width={196} height={38} />
            </div>
            <div className="flex items-start gap-8">
              <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col divide-y divide-ds-slate-100">
                <DesktopArticleRowSkeleton />
                <DesktopArticleRowSkeleton />
                <DesktopArticleRowSkeleton />
              </div>
              <div className="hidden w-desktop-rail shrink-0 flex-col gap-4 lg:flex">
                <Skeleton width={60} height={14} />
                <div className="flex flex-wrap gap-1.5">
                  {RAIL_CHIP_WIDTHS.map((chipWidth, index) => (
                    <Skeleton key={index} width={chipWidth} height={24} />
                  ))}
                </div>
                <div className="flex flex-col gap-4 pt-2">
                  <Skeleton width={90} height={14} />
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex gap-2.5">
                      <Skeleton
                        className="shrink-0 rounded-lg"
                        width={56}
                        height={56}
                      />
                      <div className="flex flex-1 flex-col gap-2 pt-1">
                        <Skeleton width="85%" height={14} />
                        <Skeleton width="65%" height={14} />
                        <Skeleton width={60} height={10} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mirrors DesktopDiscoveryPage while its feed loads. */
function ExploreSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col">
      <TopNavSkeleton contextWidth={160} />
      <div className="px-10 pt-8 pb-10">
        <div className="mx-auto flex max-w-desktop-block gap-8">
          <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col">
            <div className="mb-4 flex gap-2">
              {TAB_CHIP_WIDTHS.map((chipWidth, index) => (
                <Skeleton
                  key={index}
                  $variant="circular"
                  width={chipWidth}
                  height={32}
                />
              ))}
            </div>
            <div className="flex flex-col divide-y divide-ds-slate-100">
              <DesktopArticleHeroSkeleton />
              <DesktopArticleRowSkeleton />
              <DesktopArticleRowSkeleton />
              <DesktopArticleRowSkeleton />
            </div>
          </div>
          <div className="hidden w-desktop-rail shrink-0 flex-col gap-6 lg:flex">
            <Skeleton className="rounded-2xl" width="100%" height={240} />
            <Skeleton className="rounded-2xl" width="100%" height={180} />
          </div>
        </div>
      </div>
    </div>
  );
}
